import {ResponsivePie} from "@nivo/pie";
import PropTypes from "prop-types";
import theme from "./theme";
import lang from "../../core/lang";
import React from "react";
import dom from "../../core/dom";
import {useTheme} from "@material-ui/core";
import palette from "../../core/palette";

const DEFAULT_LEGEND = {
    anchor: 'bottom',
    direction: 'row',
    justify: false,
    translateX: 0,
    translateY: 56,
    itemsSpacing: 0,
    itemWidth: 80,
    itemHeight: 18,
    itemTextColor: '#999',
    itemDirection: 'left-to-right',
    itemOpacity: 1,
    symbolSize: 18,
    symbolShape: 'circle',
    effects: [
        {
            on: 'hover',
            style: {
                itemTextColor: '#000'
            }
        }
    ]
}
const DEFAULT_COLORS = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1', '#ff9da7', '#9c755f',  '#bab0ac'];

function resolveTheme(custom = {}, {fontSize, muiTheme}) {
    const resolved = lang.clone({...theme, ...custom});
    resolved.text.fill = muiTheme.palette.text.primary;
    if(fontSize) {
        resolved.text.fontSize = dom.scaleFont(fontSize, 0.8);
    }
    return resolved;
}


function PieChart(props) {
    const { fontSize, data, indexBy, valueBy, colors, innerRadius, margin, padAngle, cornerRadius, borderWidth, borderColor,
         enableLabel, enableLegend, isInteractive, metric} = props;
    const muiTheme = useTheme();
    const theme = resolveTheme(props.theme, {fontSize, muiTheme});
    const legends = transformLegends(enableLegend);
    const enableArcLabels = lang.isNullOrUndefined(props.enableArcLabels) ? enableLabel : props.enableArcLabels;
    const enableArcLinkLabels = lang.isNullOrUndefined(props.enableArcLinkLabels) ? enableLabel : props.enableArcLinkLabels;
    const centerMetric = lang.isFunction(metric) ? CenteredMetric(metric, {fontSize: dom.scaleFont(theme.text.fontSize, 1.2)}) : undefined;
    return (
        <ResponsivePie
            data={data}
            id={indexBy}
            value={valueBy}
            theme={theme}
            colors={colors}
            margin={margin}
            innerRadius={innerRadius}
            padAngle={padAngle}
            cornerRadius={cornerRadius}
            activeOuterRadiusOffset={8}
            borderWidth={borderWidth}
            borderColor={borderColor}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{
                from: 'color',
                modifiers: [
                    [
                        'darker',
                        2
                    ]
                ]
            }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{
                from: 'color',
                modifiers: [
                    [
                        'darker',
                        2
                    ]
                ]
            }}
            defs={[
                {
                    id: 'dots',
                    type: 'patternDots',
                    background: 'inherit',
                    color: 'rgba(255, 255, 255, 0.3)',
                    size: 4,
                    padding: 1,
                    stagger: true
                },
                {
                    id: 'lines',
                    type: 'patternLines',
                    background: 'inherit',
                    color: 'rgba(255, 255, 255, 0.3)',
                    rotation: -45,
                    lineWidth: 6,
                    spacing: 10
                }
            ]}
            // fill={[
            //     {
            //         match: {
            //             id: 'ruby'
            //         },
            //         id: 'dots'
            //     },
            //     {
            //         match: {
            //             id: 'c'
            //         },
            //         id: 'dots'
            //     },
            //     {
            //         match: {
            //             id: 'go'
            //         },
            //         id: 'dots'
            //     },
            //     {
            //         match: {
            //             id: 'python'
            //         },
            //         id: 'dots'
            //     },
            //     {
            //         match: {
            //             id: 'scala'
            //         },
            //         id: 'lines'
            //     },
            //     {
            //         match: {
            //             id: 'lisp'
            //         },
            //         id: 'lines'
            //     },
            //     {
            //         match: {
            //             id: 'elixir'
            //         },
            //         id: 'lines'
            //     },
            //     {
            //         match: {
            //             id: 'javascript'
            //         },
            //         id: 'lines'
            //     }
            // ]}
            enableArcLabels={enableArcLabels}
            enableArcLinkLabels={enableArcLinkLabels}
            isInteractive={isInteractive}
            legends={legends}
            layers={['arcs', 'arcLabels', 'arcLinkLabels', 'legends', centerMetric]}
        />
    )
}

const CenteredMetric = (metric, {fontSize}) => (options) => {
    const { dataWithArc, centerX, centerY } = options;
    const value = metric({...options, data: dataWithArc});
    let content;
    if(React.isValidElement(value)) {
        content = value;
    } else {
        content = (
            <text
                x={centerX}
                y={centerY}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                    fill:"#333333",
                    fontSize: fontSize
                }}
            >
                {value}
            </text>
        );
    }

    return content;
}

function transformLegends(enableLegend) {
    return enableLegend ? [DEFAULT_LEGEND] : undefined;
}

PieChart.propTypes = {
    theme: PropTypes.object,
    fontSize: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    indexBy: PropTypes.string,
    valueBy: PropTypes.string,
    innerRadius: PropTypes.number,
    padAngle: PropTypes.number,
    cornerRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    isInteractive: PropTypes.bool,
    enableLegend: PropTypes.bool,
    enableLabel: PropTypes.bool,
    enableArcLabels: PropTypes.bool,
    enableArcLinkLabels: PropTypes.bool,
    colors: PropTypes.arrayOf(PropTypes.string),
    margin: PropTypes.object,
    metric: PropTypes.func
};

PieChart.defaultProps = {
    indexBy: 'id',
    valueBy: 'value',
    colors: DEFAULT_COLORS,
    borderWidth: 1,
    borderColor: {
        from: 'color',
            modifiers: [
            [
                'darker',
                0.2
            ]
        ]
    },
    isInteractive: true,
    enableLegend: false
}

export default PieChart;